// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { LazyMotion, domAnimation } from 'framer-motion';
import { Toaster } from 'sonner';
import { Button } from './components/Button';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorModal } from './components/ErrorModal';
import { GameBoardRouter } from './components/GameBoardRouter';
import { GlobalHeader } from './components/GlobalHeader';
import { InstallPrompt } from './components/InstallPrompt';
import { ScrollToTop } from './components/landing/ScrollToTop';
import { Lobby } from './components/Lobby';
import { MagicLink } from './components/MagicLink';
import { Modal } from './components/Modal';
import { OfflineIndicator } from './components/OfflineIndicator';
import { RoomList } from './components/RoomList';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { useFontLoader } from './hooks/useFontLoader';
import { useGlobalKeyboardShortcuts } from './hooks/useGlobalKeyboardShortcuts';
import { useSessionRestoration } from './hooks/useSessionRestoration';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { api } from './lib/api';
import { logger } from './lib/logger';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { SimpleTest } from './pages/SimpleTest';
import { useAuthStore } from './store/authStore';
import { useGameStore } from './store/gameStore';
import { useThemeStore } from './store/themeStore';
// Lazy load infrequently accessed pages for code splitting
const AdminDashboard = lazy(() =>
  import('./components/AdminDashboard').then(m => ({
    default: m.AdminDashboard,
  }))
);
const SettingsPage = lazy(() =>
  import('./components/SettingsPage').then(m => ({
    default: m.SettingsPage,
  }))
);
const SpectatePage = lazy(() =>
  import('./pages/SpectatePage').then(m => ({ default: m.SpectatePage }))
);
const SpectatorJoinForm = lazy(() =>
  import('./components/SpectatorJoinForm').then(m => ({
    default: m.SpectatorJoinForm,
  }))
);
const TournamentPage = lazy(() =>
  import('./pages/TournamentPage').then(m => ({ default: m.TournamentPage }))
);
const CardMechanicsTest = lazy(() =>
  import('./pages/CardMechanicsTest').then(m => ({
    default: m.CardMechanicsTest,
  }))
);
const StatusComponentsTest = lazy(() =>
  import('./pages/StatusComponentsTest').then(m => ({
    default: m.StatusComponentsTest,
  }))
);
const TestIndex = lazy(() =>
  import('./pages/TestIndex').then(m => ({
    default: m.TestIndex,
  }))
);

const AppView = {
  ROOM_LIST: 0,
  LOBBY: 1,
  GAME: 2,
} as const;
type AppView = (typeof AppView)[keyof typeof AppView];

/** Global signed out modal - shared by UserMenu and keyboard shortcuts */
function SignedOutModal() {
  const { showSignedOutModal, setShowSignedOutModal } = useAuthStore();

  return (
    <Modal
      isOpen={showSignedOutModal}
      onClose={() => setShowSignedOutModal(false)}
      title="Signed Out"
    >
      <div className="text-center py-4">
        <div className="text-6xl mb-4">👋</div>
        <p className="text-lg mb-6" style={{ color: 'var(--color-text-primary)' }}>
          You have been successfully signed out.
        </p>
        <Button onClick={() => setShowSignedOutModal(false)} variant="primary" className="mx-auto">
          OK
        </Button>
      </div>
    </Modal>
  );
}

export function MainApp() {
  const { sessionId, playerId, gameState, sessionRestorePending } = useGameStore();
  const location = useLocation();

  useSessionRestoration();

  // Debug: Log when MainApp mounts/unmounts
  useEffect(() => {
    logger.debug('[MainApp] 🟢 MOUNTED', {
      sessionId,
      playerId,
      pathname: location.pathname,
    });
    return () => {
      logger.debug('[MainApp] 🔴 UNMOUNTING', {
        sessionId,
        playerId,
        pathname: location.pathname,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only on mount/unmount

  // Derive view from URL and state
  const view = (() => {
    // URL-based routing takes precedence
    if (location.pathname.startsWith('/lobby/')) {
      // Trust the URL - if user is on /lobby/:id, show lobby
      // The Lobby component will handle fetching session data if needed
      return AppView.LOBBY;
    }
    if (location.pathname.startsWith('/game/')) {
      // Trust the URL - if user is on /game/:id, show game
      // The GameBoardRouter component will handle fetching session data if needed
      return AppView.GAME;
    }

    // Fallback to state-based routing (for backward compatibility)
    if (location.pathname === '/admin') return AppView.ROOM_LIST;
    if (!sessionId) return AppView.ROOM_LIST;
    if (gameState) {
      // Check both room_status and phase for lobby detection
      // Some games use room_status, others use phase
      // Consider it lobby phase ONLY if both room_status AND phase are 'lobby' (or unset)
      const extendedState = gameState as unknown as { phase?: string };
      const roomStatus = gameState.room_status;
      const phase = extendedState?.phase;

      // If phase is set and not 'lobby', game has started
      if (phase && phase !== 'lobby') return AppView.GAME;
      // Otherwise fall back to room_status
      if (roomStatus && roomStatus !== 'lobby') return AppView.GAME;
      // Both are 'lobby' or unset - show lobby
      return AppView.LOBBY;
    }
    return AppView.ROOM_LIST;
  })();

  // This callback is a no-op since view is derived from store state
  const handleRoomJoined = () => {
    // View will update automatically when store.sessionId and store.gameState change
  };

  // Check if we should show game views (have session)
  const hasSession = Boolean(sessionId && playerId);

  // Render views - WebSocket is managed by WebSocketProvider context
  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-white focus:text-black"
      >
        Skip to main content
      </a>
      <main id="main-content">
        {(view === AppView.LOBBY || view === AppView.GAME) && sessionRestorePending && !hasSession && (
          <div className="flex min-h-[50vh] items-center justify-center">
            Restoring session...
          </div>
        )}
        {view === AppView.ROOM_LIST && <RoomList onRoomJoined={handleRoomJoined} />}
        {view === AppView.LOBBY && hasSession && <Lobby />}
        {view === AppView.GAME && hasSession && <GameBoardRouter />}
      </main>
    </div>
  );
}

function AdminRoute() {
  const navigate = useNavigate();
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">Loading admin...</div>
      }
    >
      <AdminDashboard onExit={() => navigate('/')} />
    </Suspense>
  );
}

function SettingsRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">Loading settings...</div>
      }
    >
      <SettingsPage />
    </Suspense>
  );
}

function SpectatorJoinRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading spectator...
        </div>
      }
    >
      <SpectatorJoinForm />
    </Suspense>
  );
}

function SpectateRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading spectator mode...
        </div>
      }
    >
      <SpectatePage />
    </Suspense>
  );
}

function TournamentRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">Loading tournaments...</div>
      }
    >
      <TournamentPage />
    </Suspense>
  );
}

function CardMechanicsTestRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading card test...
        </div>
      }
    >
      <CardMechanicsTest />
    </Suspense>
  );
}

function StatusComponentsTestRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading status components test...
        </div>
      }
    >
      <StatusComponentsTest />
    </Suspense>
  );
}

function TestIndexRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading test hub...
        </div>
      }
    >
      <TestIndex />
    </Suspense>
  );
}

/**
 * Component that enables global keyboard shortcuts.
 * Must be rendered inside BrowserRouter to use navigation.
 */
function GlobalKeyboardShortcuts() {
  useGlobalKeyboardShortcuts();
  return null;
}

function App() {
  const { initializeTheme } = useThemeStore();
  const { getAccessToken, isAuthenticated } = useAuthStore();
  const fontsLoaded = useFontLoader();

  useTokenRefresh();

  // Wire up auth token getter to API client
  useEffect(() => {
    api.setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Initialize theme from localStorage on app load
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  // Refresh user profile on app load to get latest profile data (emoji, color)
  useEffect(() => {
    if (!isAuthenticated()) return;

    api
      .getProfile()
      .then(profile => {
        // Update user profile in auth store with fresh data
        const { updateUserProfile } = useAuthStore.getState();
        updateUserProfile({
          profile_emoji: profile.profile_emoji,
          theme_primary_color: profile.theme_primary_color,
          theme_preference: profile.theme_preference,
        });
      })
      .catch(() => {
        // Silently ignore - user may be offline or token expired
      });
  }, [isAuthenticated]);

  // Wait for fonts to load to prevent FOUT
  if (!fontsLoaded) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-background)',
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      <LazyMotion features={domAnimation}>
        <BrowserRouter>
          <GlobalKeyboardShortcuts />
          <WebSocketProvider>
            <Toaster position="top-right" richColors />
            <ErrorModal />
            <SignedOutModal />
            <GlobalHeader />
            <OfflineIndicator />
            <InstallPrompt />
            <ScrollToTop />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/admin" element={<AdminRoute />} />
              <Route path="/settings" element={<SettingsRoute />} />
              <Route path="/magic/:roomCode" element={<MagicLink />} />
              <Route path="/spectate/join" element={<SpectatorJoinRoute />} />
              <Route path="/spectate/:sessionId" element={<SpectateRoute />} />
              <Route path="/tournaments" element={<TournamentRoute />} />
              <Route path="/test/cards" element={<CardMechanicsTestRoute />} />
              <Route path="/test/status" element={<StatusComponentsTestRoute />} />
              <Route path="/test" element={<TestIndexRoute />} />
              <Route path="/simple-test" element={<SimpleTest />} />
              {/* Single route for both lobby and game to prevent unmounting during transitions */}
              <Route path="/:view/:sessionId" element={<MainApp />} />
              <Route path="*" element={<MainApp />} />
            </Routes>
          </WebSocketProvider>
        </BrowserRouter>
      </LazyMotion>
    </ErrorBoundary>
  );
}

export default App;
